import { DotLottieWorkerReact } from '@lottiefiles/dotlottie-react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { BackButton, MainButton, UserLevelProgress } from '@ds'
import { FlipScore, MainLayout, Spiral, useStarAnim, useUserSpiral } from '@shared'
import {
  MAX_AVAILABLE_PAGE,
  PrologueStage,
  useAppActions,
  useAppIsLoadingState,
  useAvailableQuestsActions,
  useAvailableQuestsIsLoading,
  useBattlePassActions,
  usePageQuestState,
  usePrologue,
  useUserBankActions,
  useUserBankTaps,
} from '@store'

import { BottomPanel } from './BottomPanel'
import styles from './HomeView.module.scss'
import { Quests } from './Quests'
import { QuestsFallback } from './QuestsFallback'
import { DispatcherButton } from './components/DispatcherButton'
import { SpiralMove } from './components/SpiralMove'
import { ClaimButton } from './components/ClaimButton'

export function HomeView() {
  const navigate = useNavigate()
  const [isAnimated, setIsAnimated] = useState(false)
  const [isFadeOut, setIsFadeOut] = useState(true)
  const { spiral } = useUserSpiral()
  const { getBattlePass } = useBattlePassActions()
  const { getGetTapsBalance, getGetStarsBalance } = useUserBankActions()
  const taps = useUserBankTaps()
  const { getAvailableQuests } = useAvailableQuestsActions()
  const questsIsLoading = useAvailableQuestsIsLoading()
  const { setIsLoading } = useAppActions()
  const isLoading = useAppIsLoadingState()
  const { starAnim } = useStarAnim()

  const prologueStage = usePrologue.store.prologueStage()
  const isEyePrologueStage = prologueStage === PrologueStage.Eye
  const isDarken = isEyePrologueStage
  const pages = usePageQuestState()

  const isLastPage = useMemo(() => {
    return pages.current === MAX_AVAILABLE_PAGE
  }, [pages.current])

  const renderContent = useCallback(() => {
    if (isLastPage) {
      return (
        <div
          className={classNames(
            styles.scene,
            styles['scene__lastPage'],
            isAnimated && styles['scene__lastPage--animated']
          )}
          style={{
            transform: isAnimated
              ? `rotate(${(pages.current + 1) * 90}deg)`
              : 'rotate(0deg)',
          }}
        >
          <Spiral src={spiral?.appFiles || ''} />
          <QuestsFallback isAnimated={isAnimated} pageIndex={pages.current} />
          <div className={styles.soonContainer}>
            <div
              className={styles.soonContainerInner}
              style={{
                transform: isAnimated
                  ? `rotate(-${(pages.current + 1) * 90}deg)`
                  : 'rotate(0deg)',
              }}
            >
              <div
                className={classNames(styles.soonText, styles.soonText__title)}
              >
                Soon
              </div>
              <div
                className={classNames(
                  styles.soonText,
                  styles.soonText__description
                )}
              >
                Unlock in a few days
              </div>
              {Boolean(starAnim) && (
                <DotLottieWorkerReact
                  data={starAnim}
                  autoplay
                  loop
                  className={styles.soonStars}
                />
              )}
            </div>
          </div>

          <div
            className={classNames(styles.blur, isAnimated && styles.blurActive)}
          />
        </div>
      )
    }

    return (
      <div
        className={styles.scene}
        style={{
          transform: isAnimated
            ? `rotate(${(pages.current + 1) * 90}deg)`
            : 'rotate(0deg)',
        }}
      >
        <Spiral src={spiral?.appFiles || ''} />
        <Quests isAnimated={isAnimated} />
      </div>
    )
  }, [isLastPage, isDarken, isAnimated, pages.current, spiral?.appFiles])

  useEffect(() => {
    getAvailableQuests()
    getGetTapsBalance()
    getBattlePass()
    getGetStarsBalance()
  }, [])

  useEffect(() => {
    setIsLoading(questsIsLoading)
  }, [questsIsLoading])

  useEffect(() => {
    if (!questsIsLoading) {
      setTimeout(() => {
        setIsAnimated(true)
      }, 500)
    }
  }, [questsIsLoading])

  useLayoutEffect(() => {
    if (!isEyePrologueStage) {
      setIsFadeOut(false)
    }
    setTimeout(() => {
      setIsFadeOut(false)
    }, 2000)
  }, [isEyePrologueStage])

  const handleBPClick = useCallback(() => {
    navigate('/battle-pass')
  }, [navigate])

  if (isLoading) {
    return null
  }

  return (
    <MainLayout showNavbar={!isDarken} showInventory={!isDarken}>
      <BackButton hidden />
      <MainButton hidden />

      {isFadeOut && <div className={styles.fadeOut}></div>}
      {isDarken && (
        <div className={styles.darken} onClick={() => navigate('/quest/eye')} />
      )}

      <div
        className={classNames([
          styles.starsBg,
          isAnimated && styles.starsBgAnimated,
        ])}
      />

      <div className={styles.content}>
        {!isDarken && (
          <>
            <UserLevelProgress
              className={styles.userLevel}
              onClick={handleBPClick}
            />
            <DispatcherButton />
          </>
        )}
        {/* <div className={styles.flipScore}>
          <FlipScore />
        </div> */}
        <div className={styles.claim}>
          <ClaimButton />
        </div>
        {renderContent()}
      </div>

      {!isDarken && (
        <div className={styles.bottom}>
          <AnimatePresence mode="wait">
            <motion.div
              key={pages.current}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.7,
                ease: 'easeInOut',
                delay: 0.1,
              }}
              className={styles.spiralMove}
            >
              <SpiralMove />
            </motion.div>
          </AnimatePresence>
          {!isFadeOut && taps && taps.lastIncrement && (
            <BottomPanel
              tapsLeft={taps.balance}
              tapsTotal={taps.limit}
              refillDate={taps.lastIncrement}
              taplapsTime={taps.taplapsTime}
            />
          )}
        </div>
      )}
    </MainLayout>
  )
}
